/**
 * Shared layout for authenticated pages: top-level navbar + outlet.
 * Navbar: NiData | KPIs | งานคุณภาพ (when allowed) | หน้าแรก or รายงานอุบัติการณ์ (privileged) | (Team) | user menu
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  Activity,
  BarChart3,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  ExternalLink,
  FileText,
  FolderOpen,
  HeartPulse,
  Megaphone,
  Menu,
  Shield,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useAppHomePath } from '../hooks/useAppHomePath';
import { appPathsAreSame } from '../lib/appHomePath';
import { isTGOwner } from '../lib/occConstants';
import {
  ETHIC_TEAM_LABEL,
  MED_ERROR_INCIDENT_LABEL,
  MED_ERROR_TEAM_FULL_LABEL,
} from '../lib/complianceCategoryTypes';
import { getUserAssignedDepartmentIds } from '../lib/userDepartmentScope';
import { APP_HOME_SAME_ROUTE_EVENT, REPORT_SAME_ROUTE_EVENT, scrollShellContentToTop } from '../lib/appShellEvents';
import { NurseNotesShellPeriodControls } from './NurseNotesShellPeriodControls';
import {
  canAccessNurseNotesTeamDashboard,
  canViewDepartmentScopedIncidentReportNav,
  canViewEthicTeamSection,
  canViewQualityWorkNav,
  isHeadWardNurseNavUser,
  isManagerAuditorOrAdminForTeamDashboard,
  isPrivilegedIncidentNavRole,
  isSupervisorNurseNavUser,
} from '../lib/privilegedIncidentNavRole';
import { QUALITY_WORK_LINKS, sortQualityWorkLinks } from '../lib/compliance_constants';
import { QUALITY_REPORTS_NAV_LABEL } from '../features/quality-reports/qualityReportsCopy';
import { useDepartments } from '../contexts/useDepartments';
import { useNavShellFilters } from '../contexts/NavShellFiltersContext';
import { normalizeDepartmentId } from '../utils/normalizeIds';
import {
  useIncidentNavDepartmentFilterOptions,
  useTeamNavDepartmentFilterOptions,
} from '../hooks/useNavShellDepartmentOptions';
import { TeamDepartmentFilterBar } from './TeamDepartmentFilterBar';
import { ShellIncidentExportNav } from './ShellIncidentExportNav';
import { hasTeamCategoryHospitalWideAccess } from '../lib/reportingTeamCategoryScope';
import { canViewOrgClimateSurveys } from '../lib/orgClimateSurveys/access';
import { canAccessNsoPublicSite } from '../features/nso-public/access';
import { IntraSiteSearch } from './IntraSiteSearch';

/** Compliance dashboard (รายงานรวม) with category — same data as /team but without team_roles gate */
const TEAM_DESTINATION_NN = { key: 'nn', path: '/report?category=nurse_notes', label: 'บันทึกทางการพยาบาล' } as const;
const TEAM_DESTINATION_ETHIC = { key: 'ethic', path: '/report?category=ethicSurveys', label: ETHIC_TEAM_LABEL } as const;
const TEAM_DESTINATION_ORG_CLIMATE = {
  key: 'org_climate',
  path: '/report?category=orgClimateSurveys',
  label: 'บรรยากาศจริยธรรมในองค์การพยาบาล',
} as const;
const TEAM_DESTINATION_SIREN = {
  key: 'siren',
  path: '/report?category=siren_bundle',
  label: 'SIREN bundle',
} as const;
const TEAM_DESTINATION_MED_ERROR = {
  key: 'med_error_incident',
  path: '/med-error-incidents',
  label: 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา',
} as const;
const TEAM_DESTINATION_FALL = {
  key: 'fall_incident',
  path: '/fall-incidents',
  label: 'จำนวนอุบัติการณ์การพลัดตกหกล้ม',
} as const;
const TEAM_DESTINATION_KPOA = {
  key: 'kpoa',
  path: '/kpoa-incidents',
  label: 'คณะทำงานคปอ.',
} as const;
const TEAM_DESTINATION_TG = { key: 'tg', path: '/team/occ', label: 'โครงการ TG' } as const;

const NSO_NURSING_HOMEPAGE_PATH = '/nso' as const;
const PRODUCTIVITY_LOOKER_DASHBOARD_URL =
  'https://lookerstudio.google.com/reporting/d53207d9-1bfc-4d58-8fae-83363d3c72bb/page/pyJaF' as const;

/** Order of rows in คณะทำงาน mega menu (reference layout: med → safety → risk → ethic → records → TG). */
const TEAM_MEGA_RAIL_ORDER: readonly string[] = [
  'med_error_incident',
  'kpoa',
  'fall_incident',
  'ethic',
  'nn',
  'tg',
];

/** Long title + icon only (no description) for คณะทำงาน mega rail. */
function teamRailMegaMenuPresentation(dest: { key: string; label: string }): {
  label: string;
  icon: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>;
} {
  switch (dest.key) {
    case 'med_error_incident':
      return { label: MED_ERROR_TEAM_FULL_LABEL, icon: MedErrorMegaIcon };
    case 'kpoa':
      return {
        label: 'คณะทำงานพัฒนาความปลอดภัย อาชีวอนามัย และสภาพแวดล้อมในการทำงาน ฝ่ายการพยาบาล',
        icon: InjuryIllnessMegaIcon,
      };
    case 'fall_incident':
      return { label: 'คณะทำงานบริหารจัดการความเสี่ยง ฝ่ายการพยาบาล', icon: FallMegaIcon };
    case 'ethic':
      return {
        label: ETHIC_TEAM_LABEL,
        icon: EthicMegaIcon,
      };
    case 'nn':
      return {
        label: 'คณะทำงานพัฒนาระบบบันทึก ทางการพยาบาล ฝ่ายการพยาบาล',
        icon: NurseNoteMegaIcon,
      };
    case 'tg':
      return { label: 'โครงการ TG', icon: ClipboardList };
    default:
      return { label: dest.label, icon: ClipboardList };
  }
}

/** Title + description for รายงาน cards that open a compliance dashboard. */
function incidentComplianceReportCardPresentation(dest: { key: string; label: string }): {
  label: string;
  description: string;
  icon: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>;
} {
  switch (dest.key) {
    case 'nn':
      return {
        label: 'บันทึกทางการพยาบาล',
        description:
          'รายงานคุณภาพการบันทึกทางการพยาบาล\nโดย คณะทำงานพัฒนาระบบบันทึก\nทางการพยาบาล ฝ่ายการพยาบาล',
        icon: NurseNoteMegaIcon,
      };
    case 'ethic':
      return {
        label: 'บรรยากาศองค์กรจริยธรรม',
        description:
          'ผลการตอบแบบสอบถามพฤติกรรมจริยธรรมของพยาบาลและบรรยากาศจริยธรรมในสถานที่ทำงานตามการรับรู้ของพยาบาล\nโดย คณะทำงานส่งเสริมจริยธรรมวิชาชีพและพฤติกรรมบริการ ฝ่ายการพยาบาล',
        icon: EthicMegaIcon,
      };
    case 'org_climate':
      return {
        label: 'บรรยากาศจริยธรรมในองค์การพยาบาล',
        description:
          `แบบประเมิน 32 ข้อ สำหรับพยาบาล — บรรยากาศจริยธรรมในองค์การพยาบาล\n(แยกจากแบบสอบถาม${ETHIC_TEAM_LABEL} 52 ข้อ)`,
        icon: EthicMegaIcon,
      };
    case 'siren':
      return {
        label: 'SIREN bundle',
        description:
          'รายงานคุณภาพชุดปฏิบัติ SIREN\nโดย คณะทำงาน Palliative, Pain, and Pressure Injury',
        icon: HeartPulse,
      };
    default:
      return {
        label: dest.label,
        description: '',
        icon: ClipboardList,
      };
  }
}

/** Highlight shell nav for `/report?category=…` (pathname alone is not enough) */
function teamDestinationPathActive(destPath: string, pathname: string, search: string): boolean {
  const [base, queryPart] = destPath.split('?');
  if (base === '/report') {
    const want = new URLSearchParams(queryPart ?? '').get('category');
    if (!want || pathname !== '/report') return false;
    return new URLSearchParams(search).get('category') === want;
  }
  /** Standalone incident dashboards (no `/report?category=`). */
  if (
    base === '/med-error-incidents' ||
    base === '/fall-incidents' ||
    base === '/kpoa-incidents' ||
    base === '/hr' ||
    base === '/safety-culture-2569'
  ) {
    if (pathname === base) return true;
    return pathname.startsWith(`${base}/`);
  }
  if (pathname === base) return true;
  return pathname.startsWith(`${base}/`);
}

const QUALITY_WORK_SHELL_LABEL = 'งานคุณภาพ';
const TODO_QUALITY_SHELL_GRADIENT =
  'linear-gradient(90deg, rgba(107,188,219,1) 0%, rgba(163,230,191,1) 50%, rgba(237,221,83,1) 100%)';

type MegaMenuSubItem = {
  key: string;
  label: string;
  description?: string;
  to: string;
  active?: boolean;
};

type MegaMenuEntry = {
  key: string;
  label: string;
  description?: string;
  to?: string;
  href?: string;
  /** When `href` is set: navigate in this tab (no `target="_blank"`, no external-link glyph). */
  hrefSameTab?: boolean;
  icon: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  active?: boolean;
  highlight?: boolean;
  /** คณะทำงาน rail: purple title, no description line. */
  labelTone?: 'teamRail';
  badge?: 'new';
  /** Slide-down children (e.g. ethic team → ethicSurveys + orgClimateSurveys). */
  subItems?: MegaMenuSubItem[];
};

type MegaMenuCategoryKey = 'home' | 'quality' | 'incident' | 'team' | 'export';

type MegaMenuCategory = {
  key: MegaMenuCategoryKey;
  label: string;
  description: string;
  items: MegaMenuEntry[];
  badge?: 'new';
};

type MegaMenuItemLinkProps = {
  item: MegaMenuEntry;
  onSelect: () => void;
};

type MegaMenuRailButtonLinkProps = {
  item: MegaMenuEntry;
  onSelect: () => void;
  /** When set, never use the “selected” purple/white treatment (matches other rail rows). */
  alwaysNeutral?: boolean;
};

function NewBadge(): React.ReactElement {
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 py-1 pl-2 pr-2.5 text-xs font-medium text-red-600">
      <span className="mr-1 flex h-1 w-1 rounded-full bg-red-400" aria-hidden />
      ใหม่
    </span>
  );
}

/** Left-rail row: same chrome as category `<button>`s (label only, no tile icon / subtitle). */
function MegaMenuRailButtonLink({ item, onSelect, alwaysNeutral }: MegaMenuRailButtonLinkProps): React.ReactElement {
  const location = useLocation();
  const looksActive = !alwaysNeutral && item.active;
  const railClass = `block min-h-11 w-full rounded-2xl px-3 py-2.5 text-left text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 md:min-h-11 md:py-2 ${
    looksActive
      ? 'bg-white text-purple-700 shadow-sm ring-1 ring-purple-100'
      : 'text-slate-600 hover:bg-white hover:text-slate-900'
  }`;

  if (item.href) {
    return item.hrefSameTab ? (
      <a href={item.href} onClick={onSelect} className={railClass}>
        <span className="inline-flex items-center gap-2">
          <span>{item.label}</span>
          {item.badge === 'new' ? <NewBadge /> : null}
        </span>
      </a>
    ) : (
      <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={onSelect} className={railClass}>
        <span className="inline-flex items-center gap-2">
          <span>{item.label}</span>
          {item.badge === 'new' ? <NewBadge /> : null}
        </span>
      </a>
    );
  }

  const internalTo = item.to ?? '/';
  return (
    <Link
      to={internalTo}
      onClick={(event) => handleMegaMenuInternalLinkClick(internalTo, location.pathname, location.search, onSelect, event)}
      className={railClass}
    >
      <span className="inline-flex items-center gap-2">
        <span>{item.label}</span>
        {item.badge === 'new' ? <NewBadge /> : null}
      </span>
    </Link>
  );
}

/** Custom mega-menu tiles (not Lucide); omit strokeWidth so it is not forwarded to the root SVG. */
function isCustomMegaSvgIcon(
  Icon: MegaMenuEntry['icon'],
): Icon is React.ComponentType<React.SVGProps<SVGSVGElement>> {
  return (
    Icon === EthicMegaIcon ||
    Icon === FallMegaIcon ||
    Icon === NurseNoteMegaIcon ||
    Icon === InjuryIllnessMegaIcon ||
    Icon === MedErrorMegaIcon
  );
}

function qualityMegaMenuLabel(label: string): string {
  if (label === 'KPI (ทำส่งทุกไตรมาส)') return 'KPI: ทำส่งทุกไตรมาส';
  if (label === 'รายงานคุณภาพ (เดือน)') return 'รายงานคุณภาพ: รายเดือน';
  if (label === 'แผนผู้ตรวจการพยาบาลรายไตรมาส') return 'แผนผู้ตรวจการพยาบาลรายไตรมาส ๕';
  return label;
}

function qualityMegaMenuIcon(label: string): LucideIcon {
  if (label === 'KPI (ทำส่งทุกไตรมาส)') return ClipboardCheck;
  if (label === 'กิจกรรมพัฒนาคุณภาพ') return Activity;
  if (label === 'คำสั่ง/ประกาศ') return Megaphone;
  if (label === 'OP-IP Voice') return HeartPulse;
  if (label === 'Service Profile รายปี') return FileText;
  if (label.includes('แผน')) return CalendarDays;
  return ClipboardList;
}

function EthicMegaIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg viewBox="0 0 191.125 191.125" fill="currentColor" {...props}>
      <path d="M177.02,85.233c-4.787-46.353-56.53-45.631-81.462-23.259c-24.932-22.372-76.651-23.094-81.45,23.259 c-6.191,59.663,81.45,105.892,81.45,105.892S183.196,144.896,177.02,85.233z" />
      <path d="M95.558,42.368c13.642,0,81.711-1.022,81.711-21.184C177.269,1.022,109.199,0,95.558,0 c-13.642,0-81.71,1.022-81.71,21.185C13.847,41.346,81.916,42.368,95.558,42.368z M95.558,12.105 c37.977,0,61.229,5.388,68.104,9.079c-6.886,3.691-30.127,9.079-68.104,9.079c-37.974,0-61.224-5.388-68.104-9.079 C34.328,17.493,57.578,12.105,95.558,12.105z" />
    </svg>
  );
}

function FallMegaIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg viewBox="0 0 246 256" fill="currentColor" {...props}>
      <path d="M243.58,132.718V254H33.923v-42.308h67.692v-38.547h62.991v-40.427H243.58z M55.704,20.475 c0.605-9.673-6.726-17.911-16.399-18.44c-9.673-0.605-17.911,6.726-18.44,16.399c-0.529,9.673,6.802,17.911,16.399,18.44 C46.937,37.403,55.174,30.072,55.704,20.475z M90.316,130.886l34.234,34.461c3.93,4.005,10.429,4.081,14.434,0.076 c1.738-1.738,2.721-3.93,2.947-6.197c0.302-2.947-0.68-5.97-2.947-8.237l-31.211-31.287l0.756-18.288l3.627,13.603 c1.436,5.214,6.726,8.464,12.016,7.331l37.786-8.011c4.459-0.907,7.633-4.61,8.086-8.918c0.151-1.058,0.076-2.116-0.151-3.174 c-1.134-5.517-6.575-8.993-12.092-7.86l-28.264,5.97l-7.86-29.095c-0.076-0.378-0.227-0.68-0.378-1.058 c-1.209-4.081-3.779-7.633-7.255-9.9L82.078,41.635c0,0-5.214-3.023-11.26-5.441c-0.605-0.302-1.285-0.605-2.04-0.756 c-3.93-1.36-8.086-2.343-11.109-1.889c-3.476,0.529-6.348,1.965-8.011,3.401L5.372,83.88c-1.965,2.192-2.872,4.081-2.947,6.499 c-0.151,4.005,2.947,7.633,7.406,7.935c2.267,0.151,4.308-0.605,5.895-1.965l31.287-33.101l10.731,5.895L47.013,85.467 c-2.267,3.627-1.134,8.313,2.418,10.58c0.529,0.378,1.134,0.605,1.738,0.831c3.174,0.982,6.802-0.151,8.691-3.098l19.649-31.967 l4.308,2.721l-8.615,14.434l13.527,7.179l-1.436,37.182C87.217,126.2,88.275,128.921,90.316,130.886z" />
    </svg>
  );
}

function NurseNoteMegaIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg viewBox="0 0 52 52" fill="currentColor" {...props}>
      <path d="M40.8,7.7C40.8,7.7,40.8,7.7,40.8,7.7" />
      <path d="M17.2,11.6h17.6c0.9,0,1.6-0.7,1.6-1.6V6.8c0-2.6-2.2-4.8-4.8-4.8H20.4c-2.6,0-4.8,2.2-4.8,4.8V10 C15.6,10.9,16.3,11.6,17.2,11.6z" />
      <path d="M43.6,6H42c-0.5,0-0.8,0.3-0.8,0.8V10c0,3.5-2.9,6.4-6.4,6.4H17.2c-3.5,0-6.4-2.9-6.4-6.4V6.8 C10.8,6.3,10.5,6,10,6H8.4c-2.6,0-4.8,2.2-4.8,4.8v34.4c0,2.6,2.2,4.8,4.8,4.8h35.2c2.6,0,4.8-2.2,4.8-4.8V10.8 C48.4,8.2,46.2,6,43.6,6z M34,30.4L21.2,43.2h0l-5.6,1.6c-0.5,0.1-1-0.4-0.9-0.9l1.6-5.5l12.8-12.8c0.2-0.2,0.5-0.2,0.7,0l4.1,4.2 C34.2,29.9,34.2,30.2,34,30.4z M37.7,26.7l-1.6,1.6c-0.2,0.2-0.5,0.2-0.7,0l-4.2-4.2c-0.2-0.2-0.2-0.5,0-0.7l1.6-1.6 c0.7-0.8,2-0.8,2.7,0l2.1,2.1C38.5,24.7,38.5,25.9,37.7,26.7z" />
    </svg>
  );
}

function InjuryIllnessMegaIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg viewBox="0 0 512 512" fill="currentColor" {...props}>
      <polygon points="240.181,138.579 186.962,167.034 186.962,184.605 240.181,184.605" />
      <polygon points="240.181,106.481 186.962,106.481 186.962,150.195 240.181,121.739" />
      <path d="M430.677,72.518c-14.694,0-26.616,11.916-26.616,26.603v133.097h-18.079V33.059 c0-14.694-11.922-26.603-26.602-26.603c-14.694,0-26.616,11.908-26.616,26.603v199.159h-18.066V26.61 C314.697,11.916,302.775,0,288.08,0c-14.694,0-26.602,11.916-26.602,26.61v205.6h-21.296v-32.752h-53.22v59.362v31.965 c0.112,1.003,0.53,6.17-2.758,9.534c-0.891,0.982-1.88,1.672-2.73,2.069c-0.877,0.39-1.588,0.452-2.563,0.578 c-3.607,0.453-10.975-2.486-16.463-8.545c-5.473-6.031-11.964-17.543-18.441-31.018c-15.084-31.234-25.307-47.843-33.246-58.102 c-4.012-5.132-7.452-8.747-11.449-11.797c-2.02-1.525-4.206-2.89-6.811-4.039c-2.576-1.128-5.613-2.027-8.942-2.305l-2.256-0.09 c-7.424,0-14.485,3.078-19.569,8.614c-5.585,6.086-8.023,14.325-6.63,22.466c0.418,2.27,2.02,10.488,9.792,32.675 c7.772,22.16,21.727,58.206,46.923,115.464c19.528,44.264,48.888,83.117,83.57,111.05C222.674,499.339,259.137,512,300.588,512 c63.332,0,121.134-29.555,146.19-100.54c7.076-20.266,10.447-39.368,10.502-55.887c0-2.605,0-4.722,0-6.449 c0-4.686,0-6.435,0-6.456V99.12C457.28,84.433,445.357,72.518,430.677,72.518z" />
      <path d="M240.181,78.214c0-14.687-11.922-26.602-26.602-26.602c-14.695,0-26.617,11.916-26.617,26.602v13.42h53.22 V78.214z" />
    </svg>
  );
}

function MedErrorMegaIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg viewBox="0 0 512 512" fill="currentColor" {...props}>
      <path d="M471.199,231.069L271.6,33.725l0.044,0.093C249.149,11.271,219.501-0.05,189.995,0 c-29.463-0.05-59.112,11.271-81.609,33.818c-22.54,22.49-33.857,52.092-33.815,81.609c-0.042,29.502,11.274,59.147,33.815,81.645 l47.106,46.6c-1.42-0.043-2.842-0.043-4.259-0.043c-38.632,0-73.726,9.714-99.984,26.161 c-13.106,8.245-24.057,18.189-31.846,29.782c-7.832,11.55-12.417,24.835-12.417,38.858v78.719c0,14.016,4.585,27.308,12.417,38.858 c11.732,17.364,30.377,31.15,53.107,40.83c22.773,9.664,49.762,15.113,78.723,15.163c38.579-0.05,73.676-9.758,99.93-26.211 c13.149-8.202,24.054-18.188,31.89-29.781c7.793-11.55,12.374-24.842,12.374-38.858v-35.144l12.554,12.425l-0.043-0.1 c22.494,22.54,52.143,33.868,81.648,33.818c29.466,0.05,59.112-11.278,81.612-33.768c22.54-22.548,33.814-52.143,33.814-81.659 C505.013,283.212,493.739,253.609,471.199,231.069z M141.473,148.686c-19.106-19.149-19.106-50.63,0.272-70.009 c9.346-9.349,21.809-14.525,35.102-14.525L141.473,148.686z M29.896,338.43c0.043-9.026,2.839-17.637,8.482-26.075 c4.95-7.37,12.231-14.432,21.347-20.705l175.312,102.594c-3.986,2.194-8.201,4.259-12.65,6.137 c-20.113,8.567-44.672,13.608-71.152,13.608c-35.38,0.05-67.27-9.026-89.764-23.186c-11.27-7.097-20.16-15.4-26.118-24.283 c-2.205-3.305-4.033-6.645-5.456-10.08V338.43z M272.518,417.15c0,9.026-2.842,17.644-8.474,26.068 c-8.438,12.56-23.418,24.196-43.074,32.542c-19.612,8.338-43.715,13.335-69.736,13.335c-34.735,0-66.029-8.94-87.792-22.684 c-10.908-6.832-19.433-14.848-25.064-23.193c-5.642-8.424-8.439-17.042-8.482-26.068v-32.484 c11.087,12.274,26.211,22.498,44.034,30.054c22.268,9.449,48.798,14.854,77.304,14.854c37.983,0,72.44-9.586,98.005-25.623 c8.98-5.586,16.816-12.045,23.279-19.243V417.15z M272.518,356.44c-0.272,0.688-0.552,1.326-0.918,2.014 c-1.233,2.753-2.749,5.406-4.538,8.065c-3.847,5.772-9.026,11.228-15.164,16.311L78.329,281.29c1.054-0.459,2.065-1.011,3.116-1.42 c19.611-8.338,43.715-13.335,69.79-13.335c10.26,0,20.208,0.781,29.689,2.251c22.637,3.434,42.704,10.762,58.054,20.382 c1.878,1.147,3.667,2.388,5.359,3.628c8.294,5.908,14.847,12.46,19.475,19.293c0.093,0.086,0.183,0.179,0.233,0.265 c5.632,8.438,8.474,17.049,8.474,26.075V356.44z M451.77,374.9c-17.228,17.192-39.639,25.752-62.183,25.752 c-22.588,0-44.991-8.524-62.223-25.752l-0.046-0.043l-31.89-31.524v-4.903c0-14.024-4.581-27.309-12.374-38.858 c-9.801-14.432-24.326-26.39-42.016-35.553c2.201-5.728,4.994-11.966,8.432-18.368c9.714-18.246,24.286-38.041,43.578-53.247 c19.196-15.17,42.798-25.982,71.984-27.638l86.692,85.731h0.046c17.182,17.228,25.749,39.683,25.749,62.223 C477.519,335.268,468.952,357.723,451.77,374.9z" />
    </svg>
  );
}

function handleMegaMenuInternalLinkClick(
  to: string,
  pathname: string,
  search: string,
  onSelect: () => void,
  event: React.MouseEvent<HTMLAnchorElement>,
): void {
  onSelect();
  if (!teamDestinationPathActive(to, pathname, search)) return;
  event.preventDefault();
  scrollShellContentToTop();
  if (to.startsWith('/report')) {
    window.dispatchEvent(new CustomEvent(REPORT_SAME_ROUTE_EVENT, { detail: { to } }));
  }
}

function MegaMenuItemLink({ item, onSelect }: MegaMenuItemLinkProps): React.ReactElement {
  const location = useLocation();
  const Icon = item.icon;
  const LucideGlyph = Icon as LucideIcon;
  const iconGlyph = isCustomMegaSvgIcon(Icon) ? (
    <Icon className="h-7 w-7" aria-hidden />
  ) : (
    <LucideGlyph className="h-7 w-7" strokeWidth={2.1} aria-hidden />
  );
  const className = `group flex min-h-11 items-start gap-3 rounded-2xl border px-3 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 ${
    item.highlight
      ? 'border-transparent bg-gradient-to-r from-cyan-300 via-emerald-200 to-yellow-300 text-slate-900 shadow-sm hover:shadow-md'
      : item.active
      ? 'border-purple-200 bg-purple-50 text-purple-900'
      : 'border-transparent text-slate-800 hover:border-purple-100 hover:bg-purple-50'
  }`;
  const content = (
    <>
      <span
        className={`mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
          item.highlight
            ? 'bg-white/70 text-slate-800'
            : 'bg-purple-100 text-[#9448d7] group-hover:bg-purple-200'
        }`}
      >
        {iconGlyph}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`flex items-start gap-2 text-sm font-semibold leading-5 ${
            item.highlight
              ? 'text-slate-900'
              : item.active && item.labelTone === 'teamRail'
                ? 'text-purple-900'
                : item.labelTone === 'teamRail'
                  ? 'text-[#9448d7] group-hover:text-purple-800'
                  : 'text-slate-900'
          }`}
        >
          <span className="min-w-0 flex-1">{item.label}</span>
          {item.badge === 'new' ? <NewBadge /> : null}
          {item.href && !item.hrefSameTab ? (
            <ExternalLink
              className={`mt-1 h-4 w-4 shrink-0 ${item.highlight ? 'text-slate-800' : 'text-slate-400'}`}
              aria-hidden
            />
          ) : null}
        </span>
        {item.description ? (
          <span className="mt-0.5 block break-words text-xs leading-[1.15rem] text-slate-600 whitespace-pre-line">
            {item.description}
          </span>
        ) : null}
      </span>
    </>
  );

  if (item.href) {
    return item.hrefSameTab ? (
      <a href={item.href} onClick={onSelect} className={className}>
        {content}
      </a>
    ) : (
      <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={onSelect} className={className}>
        {content}
      </a>
    );
  }

  const internalTo = item.to ?? '/';
  return (
    <Link
      to={internalTo}
      onClick={(event) => handleMegaMenuInternalLinkClick(internalTo, location.pathname, location.search, onSelect, event)}
      className={className}
    >
      {content}
    </Link>
  );
}

function buildEthicClimateMegaSubItems(opts: {
  canSeeEthicReport: boolean;
  canViewOrgClimate: boolean;
  pathname: string;
  search: string;
}): MegaMenuSubItem[] {
  const items: MegaMenuSubItem[] = [];
  if (opts.canSeeEthicReport) {
    items.push({
      key: 'ethic-surveys',
      label: 'บรรยากาศองค์กรจริยธรรม',
      description: `แบบสอบถาม${ETHIC_TEAM_LABEL} 52 ข้อ`,
      to: TEAM_DESTINATION_ETHIC.path,
      active: teamDestinationPathActive(TEAM_DESTINATION_ETHIC.path, opts.pathname, opts.search),
    });
  }
  if (opts.canViewOrgClimate) {
    items.push({
      key: 'org-climate-surveys',
      label: 'บรรยากาศจริยธรรมในองค์การพยาบาล',
      description: 'แบบประเมิน 32 ข้อ สำหรับพยาบาล',
      to: TEAM_DESTINATION_ORG_CLIMATE.path,
      active: teamDestinationPathActive(TEAM_DESTINATION_ORG_CLIMATE.path, opts.pathname, opts.search),
    });
  }
  return items;
}

type MegaMenuExpandableItemLinkProps = {
  item: MegaMenuEntry;
  onSelect: () => void;
};

function MegaMenuExpandableItemLink({ item, onSelect }: MegaMenuExpandableItemLinkProps): React.ReactElement {
  const location = useLocation();
  const [expanded, setExpanded] = React.useState(() => item.subItems?.some((sub) => sub.active) ?? false);
  const Icon = item.icon;
  const LucideGlyph = Icon as LucideIcon;
  const iconGlyph = isCustomMegaSvgIcon(Icon) ? (
    <Icon className="h-7 w-7" aria-hidden />
  ) : (
    <LucideGlyph className="h-7 w-7" strokeWidth={2.1} aria-hidden />
  );
  const parentActive = item.active || item.subItems?.some((sub) => sub.active);
  const className = `group flex w-full min-h-11 items-start gap-3 rounded-2xl border px-3 py-3 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 ${
    parentActive
      ? 'border-purple-200 bg-purple-50 text-purple-900'
      : 'border-transparent text-slate-800 hover:border-purple-100 hover:bg-purple-50'
  }`;

  return (
    <div className="col-span-1 flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        className={className}
      >
        <span className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-purple-100 text-[#9448d7] group-hover:bg-purple-200">
          {iconGlyph}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`flex items-start gap-2 text-sm font-semibold leading-5 ${
              item.labelTone === 'teamRail'
                ? parentActive
                  ? 'text-purple-900'
                  : 'text-[#9448d7] group-hover:text-purple-800'
                : 'text-slate-900'
            }`}
          >
            <span className="min-w-0 flex-1">{item.label}</span>
            <ChevronDown
              className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
              strokeWidth={2.25}
              aria-hidden
            />
          </span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && item.subItems && item.subItems.length > 0 ? (
          <motion.div
            key={`${item.key}-subitems`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-4"
          >
            <div className="flex flex-col gap-1 border-l-2 border-purple-100 py-1 pl-3">
              {item.subItems.map((sub) => (
                <Link
                  key={sub.key}
                  to={sub.to}
                  onClick={(event) =>
                    handleMegaMenuInternalLinkClick(sub.to, location.pathname, location.search, onSelect, event)
                  }
                  className={`block rounded-xl px-3 py-2 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 ${
                    sub.active
                      ? 'bg-purple-50 text-purple-900 ring-1 ring-purple-100'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <span className="block text-sm font-semibold">{sub.label}</span>
                  {sub.description ? (
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500">{sub.description}</span>
                  ) : null}
                </Link>
              ))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export const AuthenticatedLayout: React.FC = () => {
  const { auth: firebaseAuth, authUser, firestoreUser, isAdmin, roleContext, roleScope, userRole } = useAuth();
  const { departments, loading: departmentsLoading } = useDepartments();
  const incidentNavDeptOptions = useIncidentNavDepartmentFilterOptions(departments);
  const {
    incidentDeptNavFilterIds,
    setIncidentDeptNavFilterIds,
    teamDeptNavFilterIds,
    setTeamDeptNavFilterIds,
    teamShellComplianceCategoryId,
    setTeamShellComplianceCategoryId,
    reportComplianceSummaryCategoryId,
    reportNurseNotesDeptNavFilterIds,
    setReportNurseNotesDeptNavFilterIds,
    reportEthicDeptNavFilterIds,
    setReportEthicDeptNavFilterIds,
  } = useNavShellFilters();
  const appHomePath = useAppHomePath();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [teamSubmenuOpen, setTeamSubmenuOpen] = useState<'med_error' | 'risk' | 'kpoa' | 'ethic' | null>(null);
  const [kpisDropdownOpen, setKpisDropdownOpen] = useState(false);
  const [incidentsDropdownOpen, setIncidentsDropdownOpen] = useState(false);
  const [qualityWorkOpen, setQualityWorkOpen] = useState(false);
  const [incidentExportMenuOpen, setIncidentExportMenuOpen] = useState(false);
  const [activeMegaMenuCategory, setActiveMegaMenuCategory] = useState<MegaMenuCategoryKey>('home');

  /**
   * When already on app home (e.g. /kpis), same-URL NavLink is a no-op — browser still shows the href “banner”.
   * Use onClickCapture on the real <a> (not a display:contents wrapper) so every engine runs this.
   */
  const onAppHomeSameRouteCapture = useCallback(
    (e: React.MouseEvent) => {
      if (!appPathsAreSame(location.pathname, appHomePath)) return;
      e.preventDefault();
      e.stopPropagation();
      setKpisDropdownOpen(false);
      setTeamDropdownOpen(false);
      setIncidentsDropdownOpen(false);
      setQualityWorkOpen(false);
      setIncidentExportMenuOpen(false);
      setMobileNavOpen(false);
      scrollShellContentToTop();
      window.dispatchEvent(new CustomEvent(APP_HOME_SAME_ROUTE_EVENT));
    },
    [location.pathname, appHomePath],
  );

  const closeNavDropdowns = useCallback(() => {
    setKpisDropdownOpen(false);
    setTeamDropdownOpen(false);
    setIncidentsDropdownOpen(false);
    setQualityWorkOpen(false);
    setIncidentExportMenuOpen(false);
  }, []);
  const closeMegaMenu = useCallback(() => {
    setMobileNavOpen(false);
    closeNavDropdowns();
  }, [closeNavDropdowns]);
  /** Keep global nav on compliance (/report) so KPI users retain คณะทำงาน + wayfinding (same app shell as /kpis) */
  const showTopNav = true;
  const mainMenuButtonRef = useRef<HTMLButtonElement>(null);
  const megaMenuPanelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileNavOpen(false);
    setKpisDropdownOpen(false);
    setTeamDropdownOpen(false);
    setIncidentsDropdownOpen(false);
    setQualityWorkOpen(false);
    setIncidentExportMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMobileNavOpen(false);
      mainMenuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  /** Close mega menu on outside click (no full-screen dimmed overlay). */
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const t = event.target as Node;
      if (mainMenuButtonRef.current?.contains(t)) return;
      if (megaMenuPanelRef.current?.contains(t)) return;
      setMobileNavOpen(false);
      mainMenuButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [mobileNavOpen]);

  const teamAllowedCategoryIds = roleContext?.teamAllowedCategoryIds ?? [];
  const hasEthicTeamRole = teamAllowedCategoryIds.includes('ethicSurveys');
  const hasNNTeamRole = useMemo(() => {
    const tokens = [roleContext?.teamRole, ...(roleContext?.teamRoles ?? [])]
      .filter(Boolean)
      .map((r) => (typeof r === 'string' ? r : (r as { role?: string }).role ?? '').toUpperCase());
    return tokens.includes('NN');
  }, [roleContext]);
  const hasOCCTeamRole = !!roleContext?.hasOCCTeamRole;
  const hasTGProjectAccess =
    isTGOwner(authUser?.uid ?? null) ||
    hasOCCTeamRole ||
    isManagerAuditorOrAdminForTeamDashboard(roleContext ?? null, isAdmin);

  const hasMedErrorIncidentAccess = useMemo(() => {
    if (teamAllowedCategoryIds.includes('med_error_incident')) return true;
    const generalRole = roleContext?.generalRole?.toUpperCase();
    const positionRole = roleContext?.positionRole?.toUpperCase();
    if (generalRole === 'ADMIN' || generalRole === 'MANAGER' || generalRole === 'AUDITOR') return true;
    if (positionRole === 'SUPERVISOR_NURSE' || positionRole === 'SN') return true;
    if (positionRole === 'HEAD_WARD_NURSE' || positionRole === 'HWN') return true;
    const deptIds = getUserAssignedDepartmentIds(firestoreUser as Record<string, unknown>);
    return deptIds.length > 0;
  }, [teamAllowedCategoryIds, roleContext, firestoreUser]);

  const hasFallIncidentAccess = useMemo(() => {
    if (teamAllowedCategoryIds.includes('fall_incident')) return true;
    const generalRole = roleContext?.generalRole?.toUpperCase();
    const positionRole = roleContext?.positionRole?.toUpperCase();
    if (generalRole === 'ADMIN' || generalRole === 'MANAGER' || generalRole === 'AUDITOR') return true;
    if (positionRole === 'SUPERVISOR_NURSE' || positionRole === 'SN') return true;
    if (positionRole === 'HEAD_WARD_NURSE' || positionRole === 'HWN') return true;
    const deptIds = getUserAssignedDepartmentIds(firestoreUser as Record<string, unknown>);
    return deptIds.length > 0;
  }, [teamAllowedCategoryIds, roleContext, firestoreUser]);

  const hasKpoaIncidentAccess = useMemo(() => {
    if (teamAllowedCategoryIds.includes('kpoa')) return true;
    const generalRole = roleContext?.generalRole?.toUpperCase();
    const positionRole = roleContext?.positionRole?.toUpperCase();
    if (generalRole === 'ADMIN' || generalRole === 'MANAGER' || generalRole === 'AUDITOR') return true;
    if (positionRole === 'SUPERVISOR_NURSE' || positionRole === 'SN') return true;
    if (positionRole === 'HEAD_WARD_NURSE' || positionRole === 'HWN') return true;
    const deptIds = getUserAssignedDepartmentIds(firestoreUser as Record<string, unknown>);
    return deptIds.length > 0;
  }, [teamAllowedCategoryIds, roleContext, firestoreUser]);

  const kpisDestinations = useMemo(() => {
    const dests: { key: string; path: string; label: string }[] = [
      { key: 'kpis', path: '/kpis', label: 'KPIs ฝ่ายการพยาบาล' },
      { key: 'hr-import', path: '/hr', label: 'ทรัพยากรบุคคล' },
    ];
    if (hasMedErrorIncidentAccess) {
      dests.push({ key: 'med-error-incident', path: '/med-error-incidents', label: MED_ERROR_INCIDENT_LABEL });
    }
    if (hasFallIncidentAccess) {
      dests.push({ key: 'fall-incident', path: '/fall-incidents', label: 'รายงานอุบัติการณ์ Fall' });
    }
    if (hasKpoaIncidentAccess) {
      dests.push({ key: 'kpoa-incident', path: '/kpoa-incidents', label: 'รายงานอุบัติการณ์ คปอ.' });
    }
    return dests;
  }, [hasMedErrorIncidentAccess, hasFallIncidentAccess, hasKpoaIncidentAccess]);

  const privilegedIncidentNav = useMemo(
    () => isPrivilegedIncidentNavRole(roleContext ?? null),
    [roleContext],
  );
  const isHeadWardNurseNav = useMemo(
    () => isHeadWardNurseNavUser(roleContext ?? null),
    [roleContext],
  );
  /** Export hub: supervisor/admin nav, and head ward nurses for their own departments. */
  const canOpenExportHub = useMemo(
    () =>
      isHeadWardNurseNav ||
      (privilegedIncidentNav && (hasMedErrorIncidentAccess || hasFallIncidentAccess)),
    [hasFallIncidentAccess, hasMedErrorIncidentAccess, isHeadWardNurseNav, privilegedIncidentNav],
  );
  const canSeeDepartmentScopedIncidentReports = useMemo(
    () => canViewDepartmentScopedIncidentReportNav(roleContext ?? null),
    [roleContext],
  );

  const isHeadWardNurse = useMemo(() => {
    const positionRole = roleContext?.positionRole?.toUpperCase();
    return positionRole === 'HEAD_WARD_NURSE' || positionRole === 'HWN';
  }, [roleContext]);

  /** Mega menu "หน้าแรก" rail: route through the shared home resolver. */
  const onMegaMenuHomeCategoryClick = useCallback(() => {
    setActiveMegaMenuCategory('home');
    const target = appHomePath;
    closeMegaMenu();
    if (appPathsAreSame(location.pathname, target)) {
      scrollShellContentToTop();
      window.dispatchEvent(new CustomEvent(APP_HOME_SAME_ROUTE_EVENT));
      return;
    }
    navigate(target);
  }, [appHomePath, closeMegaMenu, location.pathname, navigate]);

  const showQualityWorkNav = useMemo(
    () => canViewQualityWorkNav(roleContext ?? null, isAdmin),
    [roleContext, isAdmin],
  );

  const sortedQualityWorkLinks = useMemo(() => sortQualityWorkLinks(QUALITY_WORK_LINKS), []);

  /** Full hospital dept list in shell when NN/ethic team maps to the active reporting category (same on /report and /team). */
  const teamNavExpandAllDepartments = useMemo(() => {
    if (isManagerAuditorOrAdminForTeamDashboard(roleContext ?? null, isAdmin)) return true;

    const reportCat =
      location.pathname === '/report' ? new URLSearchParams(location.search).get('category') : null;

    if (reportCat && hasTeamCategoryHospitalWideAccess(roleContext ?? null, reportCat)) {
      return true;
    }

    if (
      location.pathname === '/report' &&
      reportComplianceSummaryCategoryId === 'orgClimateSurveys' &&
      canViewOrgClimateSurveys(authUser?.uid)
    ) {
      return true;
    }

    if (location.pathname.startsWith('/team/ethic')) {
      return hasTeamCategoryHospitalWideAccess(roleContext ?? null, 'ethicSurveys');
    }

    if (location.pathname === '/team') {
      const cat = teamShellComplianceCategoryId ?? 'nurse_notes';
      return hasTeamCategoryHospitalWideAccess(roleContext ?? null, cat);
    }

    return false;
  }, [location.pathname, location.search, teamShellComplianceCategoryId, reportComplianceSummaryCategoryId, roleContext, isAdmin, authUser?.uid]);

  const teamNavDeptOptions = useTeamNavDepartmentFilterOptions(departments, roleScope, roleContext, {
    expandToAllDepartments: teamNavExpandAllDepartments,
  });
  const searchBaseDeptOptions = useTeamNavDepartmentFilterOptions(departments, roleScope, roleContext, {
    expandToAllDepartments: false,
  });

  useEffect(() => {
    if (location.pathname.startsWith('/team/ethic') || !location.pathname.startsWith('/team')) {
      setTeamShellComplianceCategoryId(null);
    }
  }, [location.pathname, setTeamShellComplianceCategoryId]);

  /** Drop shell selections outside the current option set (e.g. category switched from nurse_notes to med_error). */
  useEffect(() => {
    if (!teamDeptNavFilterIds.length) return;
    const allowed = new Set(teamNavDeptOptions.map((o) => normalizeDepartmentId(o.id)));
    if (!allowed.size) return;
    const next = teamDeptNavFilterIds.filter((id) => allowed.has(normalizeDepartmentId(id)));
    if (next.length !== teamDeptNavFilterIds.length) {
      setTeamDeptNavFilterIds(next.length ? next : []);
    }
  }, [teamNavDeptOptions, teamDeptNavFilterIds, setTeamDeptNavFilterIds]);

  useEffect(() => {
    if (!reportNurseNotesDeptNavFilterIds.length) return;
    const allowed = new Set(teamNavDeptOptions.map((o) => normalizeDepartmentId(o.id)));
    if (!allowed.size) return;
    const next = reportNurseNotesDeptNavFilterIds.filter((id) =>
      allowed.has(normalizeDepartmentId(id)),
    );
    if (next.length !== reportNurseNotesDeptNavFilterIds.length) {
      setReportNurseNotesDeptNavFilterIds(next.length ? next : []);
    }
  }, [
    teamNavDeptOptions,
    reportNurseNotesDeptNavFilterIds,
    setReportNurseNotesDeptNavFilterIds,
  ]);

  useEffect(() => {
    if (!reportEthicDeptNavFilterIds.length) return;
    const allowed = new Set(teamNavDeptOptions.map((o) => normalizeDepartmentId(o.id)));
    if (!allowed.size) return;
    const next = reportEthicDeptNavFilterIds.filter((id) => allowed.has(normalizeDepartmentId(id)));
    if (next.length !== reportEthicDeptNavFilterIds.length) {
      setReportEthicDeptNavFilterIds(next.length ? next : []);
    }
  }, [teamNavDeptOptions, reportEthicDeptNavFilterIds, setReportEthicDeptNavFilterIds]);

  /** Sub-header filter: routes reached from the "รายงานอุบัติการณ์" nav dropdown only. */
  const showShellIncidentDeptFilter = useMemo(
    () =>
      privilegedIncidentNav &&
      incidentNavDeptOptions.length > 1 &&
      (location.pathname.startsWith('/med-error-incidents') ||
        location.pathname.startsWith('/fall-incidents') ||
        location.pathname.startsWith('/kpoa-incidents')),
    [privilegedIncidentNav, incidentNavDeptOptions.length, location.pathname],
  );

  /** Sub-header filter: team-result pages; `/report` nurse_notes uses sticky shell row (same bar styling as /team). */
  const showShellTeamDeptFilter = useMemo(() => {
    if (!canSeeDepartmentScopedIncidentReports || teamNavDeptOptions.length <= 1) return false;
    if (location.pathname === '/team' || location.pathname.startsWith('/team/ethic')) return true;
    return false;
  }, [canSeeDepartmentScopedIncidentReports, teamNavDeptOptions.length, location.pathname]);

  /** `/report` รายงานรวม + nurse_notes: sticky dept filter (ComplianceHub publishes category via NavShell). */
  const showShellReportNurseNotesDeptFilter = useMemo(
    () =>
      location.pathname === '/report' &&
      reportComplianceSummaryCategoryId === 'nurse_notes' &&
      teamNavDeptOptions.length > 1,
    [location.pathname, reportComplianceSummaryCategoryId, teamNavDeptOptions.length],
  );

  /** `/report` รายงานรวม + ethicSurveys or orgClimateSurveys: sticky dept filter (same shell row as nurse_notes). */
  const showShellReportEthicDeptFilter = useMemo(
    () =>
      location.pathname === '/report' &&
      (reportComplianceSummaryCategoryId === 'ethicSurveys' ||
        reportComplianceSummaryCategoryId === 'orgClimateSurveys') &&
      teamNavDeptOptions.length > 1,
    [location.pathname, reportComplianceSummaryCategoryId, teamNavDeptOptions.length],
  );

  const showShellNurseNotesPeriodControls = useMemo(
    () => location.pathname === '/report' && reportComplianceSummaryCategoryId === 'nurse_notes',
    [location.pathname, reportComplianceSummaryCategoryId],
  );

  const showPrivilegedShellDeptFilterRow =
    showShellIncidentDeptFilter ||
    showShellTeamDeptFilter ||
    showShellReportNurseNotesDeptFilter ||
    showShellReportEthicDeptFilter ||
    showShellNurseNotesPeriodControls;

  const incidentReportDestinations = useMemo(() => {
    const dests: { key: string; path: string; label: string }[] = [];
    if (hasMedErrorIncidentAccess) {
      dests.push({
        key: 'med-error-incident',
        path: '/med-error-incidents',
        label: MED_ERROR_INCIDENT_LABEL,
      });
    }
    if (hasFallIncidentAccess) {
      dests.push({
        key: 'fall-incident',
        path: '/fall-incidents',
        label: 'รายงานอุบัติการณ์ Fall',
      });
    }
    if (hasKpoaIncidentAccess) {
      dests.push({
        key: 'kpoa-incident',
        path: '/kpoa-incidents',
        label: 'รายงานอุบัติการณ์ คปอ.',
      });
    }
    return dests;
  }, [hasMedErrorIncidentAccess, hasFallIncidentAccess, hasKpoaIncidentAccess]);

  const canSeeEthicTeamNav = useMemo(() => canViewEthicTeamSection(roleContext ?? null), [roleContext]);
  const canSeeEthicReportNav = canSeeEthicTeamNav || canSeeDepartmentScopedIncidentReports;
  const canViewOrgClimate = canViewOrgClimateSurveys(authUser?.uid);

  const teamDestinations = useMemo(() => {
    const dests: { key: string; path: string; label: string }[] = [];
    const privilegedNav = isPrivilegedIncidentNavRole(roleContext ?? null);

    // /report is the department-scoped surface for HWN and LOF/global-scoped for elevated roles.
    if (canSeeDepartmentScopedIncidentReports || canAccessNurseNotesTeamDashboard(roleContext ?? null, isAdmin)) {
      dests.push(TEAM_DESTINATION_NN);
    }
    if (canSeeEthicReportNav) {
      dests.push(TEAM_DESTINATION_ETHIC);
    }
    if (privilegedNav) {
      dests.push(TEAM_DESTINATION_MED_ERROR);
      dests.push(TEAM_DESTINATION_FALL);
      if (hasKpoaIncidentAccess) {
        dests.push(TEAM_DESTINATION_KPOA);
      }
    }
    if (hasTGProjectAccess) {
      dests.push(TEAM_DESTINATION_TG);
    }
    return dests;
  }, [roleContext, isAdmin, canSeeDepartmentScopedIncidentReports, canSeeEthicReportNav, hasKpoaIncidentAccess, hasTGProjectAccess]);

  const displayName =
    typeof firestoreUser?.displayName === 'string'
      ? firestoreUser.displayName.trim()
      : typeof authUser?.displayName === 'string'
        ? authUser.displayName.trim()
        : authUser?.email ?? 'Member';
  const userInitial = displayName.trim()?.[0]?.toUpperCase() || '?';

  const primaryMegaMenuItem = useMemo<MegaMenuEntry>(
    () =>
      ({
        key: 'kpis',
        label: 'KPIs ฝ่ายการพยาบาล',
        description: 'Dashboard แสดงตัวชี้วัดและผลการดำเนินงานตามตัวชี้วัดของฝ่ายการพยาบาล',
        to: '/kpis',
        icon: BarChart3,
        active: location.pathname === '/kpis' || location.pathname.startsWith('/kpis/'),
      }),
    [location.pathname],
  );

  const nsoHomepageMegaMenuItem = useMemo<MegaMenuEntry | null>(() => {
    if (!canAccessNsoPublicSite(authUser?.uid)) return null;
    return {
      key: 'nso-nursing-homepage',
      label: 'Homepage ฝ่ายการพยาบาล',
      to: NSO_NURSING_HOMEPAGE_PATH,
      icon: ExternalLink,
      badge: 'new',
    };
  }, [authUser?.uid]);

  const productivityTeamMegaMenuItem = useMemo<MegaMenuEntry>(
    () => ({
      key: 'productivity-team-looker',
      label: 'คณะทำงานพัฒนาระบบบริหารอัตรากำลังทางการพยาบาล (Productivity)',
      href: PRODUCTIVITY_LOOKER_DASHBOARD_URL,
      icon: BarChart3,
      labelTone: 'teamRail' as const,
      badge: 'new',
    }),
    [],
  );

  /** Top-level mega-menu rail link (same row as คณะทำงาน) — TG is easy to miss when nested only under that category. */
  const tgProjectMegaMenuRailItem = useMemo<MegaMenuEntry | null>(() => {
    if (!hasTGProjectAccess) return null;
    return {
      key: 'tg-project-rail',
      label: 'โครงการ TG',
      to: TEAM_DESTINATION_TG.path,
      icon: ClipboardList,
      active: teamDestinationPathActive(TEAM_DESTINATION_TG.path, location.pathname, location.search),
    };
  }, [hasTGProjectAccess, location.pathname, location.search]);

  const productivityIncidentMegaMenuItem = useMemo<MegaMenuEntry>(
    () => ({
      key: 'productivity-incident-looker',
      label: 'Productivity Dashboard',
      description: 'แดชบอร์ดอัตรากำลังทางการพยาบาล (Looker Studio)',
      href: PRODUCTIVITY_LOOKER_DASHBOARD_URL,
      icon: BarChart3,
      badge: 'new',
    }),
    [],
  );

  const qualityMegaMenuItems = useMemo<MegaMenuEntry[]>(() => {
    const inAppQuality: MegaMenuEntry = {
      key: 'quality-reports-in-app',
      label: QUALITY_REPORTS_NAV_LABEL,
      description: 'หน้าในระบบสำหรับรายงานคุณภาพ',
      to: '/quality-reports',
      icon: FolderOpen,
      active: location.pathname === '/quality-reports' || location.pathname.startsWith('/quality-reports/'),
    };
    const driveItems = sortedQualityWorkLinks.map((item) => ({
      key: item.url,
      label: qualityMegaMenuLabel(item.label),
      href: item.url,
      icon: qualityMegaMenuIcon(item.label),
      highlight: 'tier' in item && item.tier === 'todo-quality',
    }));
    return [inAppQuality, ...driveItems];
  }, [location.pathname, sortedQualityWorkLinks]);

  const incidentMegaMenuItems = useMemo<MegaMenuEntry[]>(
    () => {
      const privilegedNav = isPrivilegedIncidentNavRole(roleContext ?? null);
      const showIncidentComplianceNurseNotes =
        canSeeDepartmentScopedIncidentReports || canAccessNurseNotesTeamDashboard(roleContext ?? null, isAdmin);
      const items: Array<MegaMenuEntry | null> = [
        showIncidentComplianceNurseNotes
          ? {
              key: 'incident-panel-nurse-notes',
              ...incidentComplianceReportCardPresentation(TEAM_DESTINATION_NN),
              to: TEAM_DESTINATION_NN.path,
              active: teamDestinationPathActive(TEAM_DESTINATION_NN.path, location.pathname, location.search),
            }
          : null,
        canSeeDepartmentScopedIncidentReports ||
        hasTeamCategoryHospitalWideAccess(roleContext ?? null, 'siren_bundle')
          ? {
              key: 'incident-panel-siren-bundle',
              ...incidentComplianceReportCardPresentation(TEAM_DESTINATION_SIREN),
              to: TEAM_DESTINATION_SIREN.path,
              active: teamDestinationPathActive(TEAM_DESTINATION_SIREN.path, location.pathname, location.search),
            }
          : null,
        canSeeEthicReportNav
          ? {
              key: 'incident-panel-ethic-surveys',
              ...incidentComplianceReportCardPresentation(TEAM_DESTINATION_ETHIC),
              to: TEAM_DESTINATION_ETHIC.path,
              active: teamDestinationPathActive(TEAM_DESTINATION_ETHIC.path, location.pathname, location.search),
            }
          : null,
        canViewOrgClimate
          ? {
              key: 'incident-panel-org-climate',
              ...incidentComplianceReportCardPresentation(TEAM_DESTINATION_ORG_CLIMATE),
              to: TEAM_DESTINATION_ORG_CLIMATE.path,
              active: teamDestinationPathActive(
                TEAM_DESTINATION_ORG_CLIMATE.path,
                location.pathname,
                location.search,
              ),
            }
          : null,
        hasMedErrorIncidentAccess
          ? {
              key: 'med-error-incident',
              label: MED_ERROR_INCIDENT_LABEL,
              description:
                'จำนวนอุบัติการณ์ความคลาดเคลื่อนจากการบริหารยา (HAD และยาทั่วไป) ของกลุ่มงานผู้ป่วยนอกและผู้ป่วยใน ตามตัวชี้วัดฝ่ายการพยาบาล ที่ 9–12\nโดย คณะทำงานความคลาดเคลื่อนทางยา ฝ่ายการพยาบาล',
              to: '/med-error-incidents',
              icon: MedErrorMegaIcon,
              active:
                location.pathname === '/med-error-incidents' ||
                location.pathname.startsWith('/med-error-incidents/'),
            }
          : null,
        hasFallIncidentAccess
          ? {
              key: 'fall-incident',
              label: 'รายงานอุบัติการณ์ Fall',
              description:
                'จำนวนอุบัติการณ์การพลัดตกหกล้ม ของกลุ่มงานผู้ป่วยนอกและผู้ป่วยใน ตามตัวชี้วัดฝ่ายการพยาบาล ที่ 7 และ 8\nโดย คณะทำงานบริหารจัดการความเสี่ยง ฝ่ายการพยาบาล',
              to: '/fall-incidents',
              icon: FallMegaIcon,
              active: location.pathname === '/fall-incidents' || location.pathname.startsWith('/fall-incidents/'),
            }
          : null,
        hasKpoaIncidentAccess
          ? {
              key: 'kpoa-incident',
              label: 'รายงานอุบัติการณ์จากการทำงาน',
              description:
                'จำนวนอุบัติการณ์การบาดเจ็บ/เจ็บป่วย จากการทำงาน ของเจ้าหน้าที่ฝ่ายการพยาบาล\nโดย คณะทำงานพัฒนาความปลอดภัย\nอาชีวอนามัย และสภาพแวดล้อมในการทำงาน ฝ่ายการพยาบาล',
              to: '/kpoa-incidents',
              icon: InjuryIllnessMegaIcon,
              active: location.pathname === '/kpoa-incidents' || location.pathname.startsWith('/kpoa-incidents/'),
            }
          : null,
      ];
      const filtered = items.filter((item): item is MegaMenuEntry => item !== null);
      if (filtered.length > 0) {
        const hrItem: MegaMenuEntry = {
          key: 'hr-import-dashboard',
          label: 'ทรัพยากรบุคคล',
          description: 'สรุปอัตราลาออก ดัชนีความสุข ความพึงพอใจในงาน และความผูกพัน',
          to: '/hr',
          icon: Users,
          active: location.pathname === '/hr' || location.pathname.startsWith('/hr/'),
        };
        const ethicIdx = filtered.findIndex((i) => i.key === 'incident-panel-ethic-surveys');
        const nnIdx = filtered.findIndex((i) => i.key === 'incident-panel-nurse-notes');
        const insertAt = ethicIdx >= 0 ? ethicIdx + 1 : nnIdx >= 0 ? nnIdx + 1 : filtered.length;
        filtered.splice(insertAt, 0, hrItem);
      }
      /** Static FY2569 snapshot: always available, so users with no ward still get this menu. */
      filtered.push({
        key: 'safety-culture-2569',
        label: 'วัฒนธรรมความปลอดภัย ปี 2569',
        description: 'สรุปผลแบบสำรวจวัฒนธรรมความปลอดภัยผู้ป่วยของฝ่ายการพยาบาล ปี 2569 (10 มิติ)',
        to: '/safety-culture-2569',
        icon: Shield,
        active:
          location.pathname === '/safety-culture-2569' ||
          location.pathname.startsWith('/safety-culture-2569/'),
      });
      filtered.push(productivityIncidentMegaMenuItem);
      return filtered;
    },
    [
      canSeeEthicReportNav,
      canViewOrgClimate,
      canSeeDepartmentScopedIncidentReports,
      hasFallIncidentAccess,
      hasKpoaIncidentAccess,
      hasMedErrorIncidentAccess,
      isAdmin,
      location.pathname,
      location.search,
      productivityIncidentMegaMenuItem,
      roleContext,
    ],
  );

  const teamMegaMenuItems = useMemo<MegaMenuEntry[]>(() => {
    const orderIndex = (key: string) => {
      const i = TEAM_MEGA_RAIL_ORDER.indexOf(key);
      return i === -1 ? 999 : i;
    };
    const ethicClimateSubItems = buildEthicClimateMegaSubItems({
      canSeeEthicReport: canSeeEthicReportNav,
      canViewOrgClimate,
      pathname: location.pathname,
      search: location.search,
    });
    const fromDestinations = [...teamDestinations]
      .sort((a, b) => orderIndex(a.key) - orderIndex(b.key))
      .map((dest) => {
        const p = teamRailMegaMenuPresentation(dest);
        if (dest.key === 'ethic' && ethicClimateSubItems.length > 1) {
          return {
            key: `team-${dest.key}`,
            label: p.label,
            icon: p.icon,
            labelTone: 'teamRail' as const,
            active: ethicClimateSubItems.some((sub) => sub.active),
            subItems: ethicClimateSubItems,
          };
        }
        const linkTo =
          dest.key === 'ethic' && ethicClimateSubItems.length === 1
            ? ethicClimateSubItems[0].to
            : dest.path;
        return {
          key: `team-${dest.key}`,
          label: p.label,
          to: linkTo,
          icon: p.icon,
          labelTone: 'teamRail' as const,
          active: teamDestinationPathActive(linkTo, location.pathname, location.search),
        };
      });
    return [...fromDestinations, productivityTeamMegaMenuItem];
  }, [
    canSeeEthicReportNav,
    canViewOrgClimate,
    location.pathname,
    location.search,
    productivityTeamMegaMenuItem,
    teamDestinations,
  ]);

  const exportMegaMenuItems = useMemo<MegaMenuEntry[]>(
    () => {
      const exportTabActive =
        location.pathname === '/report' && new URLSearchParams(location.search).get('tab') === 'export';
      const items: Array<MegaMenuEntry | null> = [
        canOpenExportHub
          ? {
              key: 'incident-export',
              label: 'ส่งออก',
              description: 'ส่งออก Dashboard และตารางข้อมูลจากหมวดที่เลือก เป็น Excel (.xlsx) หรือ PDF',
              to: '/report?tab=export',
              icon: FileText,
              active: exportTabActive,
            }
          : null,
      ];
      return items.filter((item): item is MegaMenuEntry => item !== null);
    },
    [canOpenExportHub, location.pathname, location.search],
  );

  const megaMenuCategories = useMemo<MegaMenuCategory[]>(() => {
    const categories: Array<MegaMenuCategory | null> = [
      {
        key: 'home',
        label: 'หน้าแรก',
        description: 'เมนูหลักของฝ่ายการพยาบาล — ลิงก์สำคัญอยู่ด้านซ้ายใต้ปุ่มนี้',
        items: [],
      },
      showQualityWorkNav && qualityMegaMenuItems.length > 0
        ? {
            key: 'quality',
            label: QUALITY_WORK_SHELL_LABEL,
            description: 'ลิงก์งานคุณภาพ เอกสาร และรายการที่ต้องติดตาม',
            items: qualityMegaMenuItems,
          }
        : null,
      incidentMegaMenuItems.length > 0
        ? {
            key: 'incident',
            label: 'รายงาน',
            description: 'รายงานอุบัติการณ์ที่เปิดตามสิทธิ์ของผู้ใช้',
            items: incidentMegaMenuItems,
          }
        : null,
      teamMegaMenuItems.length > 0
        ? {
            key: 'team',
            label: 'คณะทำงาน',
            description: 'คณะทำงานและโครงการที่เปิดตามสิทธิ์การเข้าถึงของคุณ',
            items: teamMegaMenuItems,
          }
        : null,
      exportMegaMenuItems.length > 0
        ? {
            key: 'export',
            label: 'ส่งออก',
            description: 'เมนูส่งออกข้อมูลอุบัติการณ์',
            items: exportMegaMenuItems,
          }
        : null,
    ];
    return categories.filter(
      (category): category is MegaMenuCategory =>
        category !== null && (category.items.length > 0 || category.key === 'home'),
    );
  }, [
    exportMegaMenuItems,
    incidentMegaMenuItems,
    qualityMegaMenuItems,
    showQualityWorkNav,
    teamMegaMenuItems,
  ]);

  const searchMenuLinks = useMemo(() => {
    const links = [
      primaryMegaMenuItem,
      ...(nsoHomepageMegaMenuItem ? [nsoHomepageMegaMenuItem] : []),
      ...(tgProjectMegaMenuRailItem ? [tgProjectMegaMenuRailItem] : []),
      ...megaMenuCategories.flatMap((category) => category.items),
    ];
    if (isAdmin || authUser?.email?.toLowerCase() === '5650414@nidata.com') {
      links.push(
        { key: 'admin-roles', label: 'User Administration', to: '/roles', icon: Users },
        { key: 'admin-departments', label: 'Department Administration', to: '/departments', icon: Users },
        { key: 'admin-categories', label: 'Compliance Categories', to: '/compliance-categories', icon: Users },
      );
    }
    return links;
  }, [
    authUser?.email,
    isAdmin,
    megaMenuCategories,
    nsoHomepageMegaMenuItem,
    primaryMegaMenuItem,
    tgProjectMegaMenuRailItem,
  ]);

  const searchFormVisibility = useMemo(() => {
    const supervisorNurse = isSupervisorNurseNavUser(roleContext ?? null);
    return {
      hydrationCategoryKeys: [] as string[],
      isSupervisorNurse: supervisorNurse,
      isGlobalViewRole: isManagerAuditorOrAdminForTeamDashboard(roleContext ?? null, isAdmin),
      departmentId: roleContext?.lineOfWorkDepartmentIds?.[0] ?? null,
      canViewOrgClimate: canViewOrgClimateSurveys(authUser?.uid),
      applyHeadWardReportAllowlist: isHeadWardNurse && !supervisorNurse,
    };
  }, [authUser?.uid, isAdmin, isHeadWardNurse, roleContext]);

  const activeMegaMenu = useMemo(
    () => megaMenuCategories.find((category) => category.key === activeMegaMenuCategory) ?? megaMenuCategories[0],
    [activeMegaMenuCategory, megaMenuCategories],
  );

  const megaMenuHomeRailOnly =
    activeMegaMenu?.key === 'home' && activeMegaMenu.items.length === 0;

  /** If the saved category is no longer visible (role/permissions), jump to the first visible category. */
  useEffect(() => {
    if (megaMenuCategories.length === 0) return;
    const stillVisible = megaMenuCategories.some((c) => c.key === activeMegaMenuCategory);
    if (!stillVisible) {
      setActiveMegaMenuCategory(megaMenuCategories[0].key);
    }
  }, [megaMenuCategories, activeMegaMenuCategory]);

  const teamEthicClimateDropdown = useMemo(() => {
    const subItems = buildEthicClimateMegaSubItems({
      canSeeEthicReport: canSeeEthicReportNav,
      canViewOrgClimate,
      pathname: location.pathname,
      search: location.search,
    });
    const showEthicTeamRow =
      teamDestinations.some((d) => d.key === 'ethic') || (canViewOrgClimate && subItems.length > 0);
    if (!showEthicTeamRow || subItems.length === 0) return null;

    const closeDropdown = () => {
      setTeamDropdownOpen(false);
      setTeamSubmenuOpen(null);
    };

    if (subItems.length === 1) {
      return (
        <Link
          to={subItems[0].to}
          onClick={closeDropdown}
          className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          {subItems[0].label}
        </Link>
      );
    }

    return (
      <div>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          aria-expanded={teamSubmenuOpen === 'ethic'}
          onClick={() => setTeamSubmenuOpen((cur) => (cur === 'ethic' ? null : 'ethic'))}
        >
          <span className="min-w-0 flex-1 truncate">{ETHIC_TEAM_LABEL}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${teamSubmenuOpen === 'ethic' ? 'rotate-180' : ''}`}
            strokeWidth={2.25}
            aria-hidden
          />
        </button>
        <AnimatePresence initial={false}>
          {teamSubmenuOpen === 'ethic' ? (
            <motion.div
              key="team-ethic-submenu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-t border-slate-100 bg-slate-50/80"
            >
              {subItems.map((sub) => (
                <Link
                  key={sub.key}
                  to={sub.to}
                  onClick={closeDropdown}
                  className={`block px-6 py-2 text-sm hover:bg-slate-100 ${
                    sub.active ? 'font-semibold text-purple-800' : 'text-slate-700'
                  }`}
                >
                  {sub.label}
                </Link>
              ))}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }, [
    canSeeEthicReportNav,
    canViewOrgClimate,
    location.pathname,
    location.search,
    teamDestinations,
    teamSubmenuOpen,
  ]);

  const handleSignOut = () => {
    setMenuOpen(false);
    if (firebaseAuth) signOut(firebaseAuth).catch(console.error);
  };

  const qualityWorkDesktopNav =
    showQualityWorkNav && sortedQualityWorkLinks.length > 0 ? (
      <div className="relative">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={qualityWorkOpen}
          aria-label={QUALITY_WORK_SHELL_LABEL}
          onClick={() => {
            setQualityWorkOpen((p) => !p);
            setTeamDropdownOpen(false);
            setIncidentsDropdownOpen(false);
            setKpisDropdownOpen(false);
            setIncidentExportMenuOpen(false);
          }}
          className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
            qualityWorkOpen
              ? 'bg-purple-100 text-purple-700'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
          }`}
        >
          <span>{QUALITY_WORK_SHELL_LABEL}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${qualityWorkOpen ? 'rotate-180' : ''}`}
            strokeWidth={2.25}
            aria-hidden
          />
        </button>
        {qualityWorkOpen && (
          <>
            <div
              className="fixed inset-x-0 bottom-0 top-16 z-40"
              aria-hidden
              onClick={() => setQualityWorkOpen(false)}
            />
            <div className="absolute left-0 top-full z-50 mt-1 max-h-[min(70vh,28rem)] min-w-[260px] overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              <Link
                to="/quality-reports"
                className="flex min-h-11 items-center gap-2 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                onClick={() => setQualityWorkOpen(false)}
              >
                <span className="min-w-0 flex-1">{QUALITY_REPORTS_NAV_LABEL}</span>
              </Link>
              {sortedQualityWorkLinks.map((item) => {
                const isTodo = 'tier' in item && item.tier === 'todo-quality';
                return (
                  <a
                    key={item.url}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex min-h-11 items-center gap-2 px-4 py-2 text-sm ${
                      isTodo ? 'font-semibold text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    style={isTodo ? { background: TODO_QUALITY_SHELL_GRADIENT } : undefined}
                    onClick={() => setQualityWorkOpen(false)}
                  >
                    <span className="min-w-0 flex-1">{item.label}</span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                  </a>
                );
              })}
            </div>
          </>
        )}
      </div>
    ) : null;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(1200px 600px at 10% -10%, #ffffff 0%, #f7f8ff 40%, #eef0ff 100%)' }}>
      {showTopNav && (
        <>
        <header className="sticky top-0 z-[100] flex h-16 items-center gap-2 border-b border-slate-200/80 bg-white/90 px-3 shadow-sm backdrop-blur sm:gap-3 sm:px-6">
          <div className="relative z-[200]">
            <button
              type="button"
              ref={mainMenuButtonRef}
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
              aria-expanded={mobileNavOpen}
              aria-haspopup="true"
              aria-controls="main-mega-menu"
              aria-label={mobileNavOpen ? 'ปิดเมนูหลัก' : 'เปิดเมนูหลัก'}
              onClick={() => {
                setMobileNavOpen((p) => !p);
                setMenuOpen(false);
                setKpisDropdownOpen(false);
                setTeamDropdownOpen(false);
                setIncidentsDropdownOpen(false);
                setQualityWorkOpen(false);
                setIncidentExportMenuOpen(false);
              }}
            >
              {mobileNavOpen ? <X className="h-6 w-6 shrink-0" strokeWidth={2.25} aria-hidden /> : <Menu className="h-6 w-6 shrink-0" strokeWidth={2.25} aria-hidden />}
              <span className="hidden text-sm font-semibold sm:inline">{mobileNavOpen ? 'ปิดเมนู' : 'เมนู'}</span>
              <NewBadge />
            </button>
            <AnimatePresence>
              {mobileNavOpen ? (
                <motion.nav
                  key="main-mega-menu"
                  ref={megaMenuPanelRef}
                  id="main-mega-menu"
                  role="navigation"
                  aria-label="เมนูหลัก"
                  initial={{ opacity: 0, y: -10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 30, mass: 0.7 }}
                  className="absolute left-0 top-full z-[200] mt-2 flex max-h-[min(42rem,calc(100dvh-4.5rem))] w-[min(calc(100vw-1.5rem),72rem)] max-w-[calc(100vw-1.5rem)] origin-top-left flex-col overflow-x-hidden overflow-y-auto overscroll-contain rounded-3xl border border-slate-200 bg-white p-3 shadow-2xl sm:p-4 md:h-[min(42rem,calc(100dvh-5.25rem))] md:max-h-[min(42rem,calc(100dvh-5.25rem))] md:overflow-hidden md:p-4"
                  style={{ transformOrigin: 'top left' }}
                >
                  <div className="mb-2 flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 pb-2">
                    <div>
                      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-purple-600">NiData Menu</p>
                      <h2 className="text-base font-bold text-slate-900">เมนูนำทาง</h2>
                    </div>
                    <button
                      type="button"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                      aria-label="ปิดเมนูหลัก"
                      onClick={() => {
                        setMobileNavOpen(false);
                        mainMenuButtonRef.current?.focus();
                      }}
                    >
                      <X className="h-6 w-6" strokeWidth={2.25} aria-hidden />
                    </button>
                  </div>
                  <div
                    className={`grid min-h-0 gap-3 md:flex-1 md:grid md:overflow-hidden ${
                      megaMenuHomeRailOnly ? 'md:grid-cols-1' : 'md:grid-cols-[15rem_minmax(0,1fr)]'
                    }`}
                  >
                    <div
                      className="grid min-h-0 grid-cols-2 gap-2 rounded-2xl bg-slate-50 p-2 sm:grid-cols-3 md:h-full md:max-h-full md:min-h-0 md:overflow-y-auto md:overscroll-contain md:flex md:flex-col"
                      aria-label="หมวดหมู่เมนูหลัก"
                    >
                      {megaMenuCategories.map((category) => {
                        const selected = activeMegaMenu?.key === category.key;
                        if (category.key === 'home') {
                          return (
                            <div
                              key="home-rail"
                              className="col-span-2 flex flex-col gap-2 sm:col-span-2 md:col-span-1 md:w-full"
                            >
                              <button
                                type="button"
                                className={`min-h-11 w-full rounded-2xl px-3 py-2.5 text-left text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 md:min-h-11 md:py-2 ${
                                  selected
                                    ? 'bg-white text-purple-700 shadow-sm ring-1 ring-purple-100'
                                    : 'text-slate-600 hover:bg-white hover:text-slate-900'
                                }`}
                                aria-pressed={selected}
                                aria-current={selected ? 'true' : undefined}
                                onClick={onMegaMenuHomeCategoryClick}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <span>{category.label}</span>
                                  {category.badge === 'new' ? <NewBadge /> : null}
                                </span>
                              </button>
                              <MegaMenuRailButtonLink
                                item={primaryMegaMenuItem}
                                onSelect={closeMegaMenu}
                                alwaysNeutral
                              />
                              {nsoHomepageMegaMenuItem ? (
                                <MegaMenuRailButtonLink
                                  item={nsoHomepageMegaMenuItem}
                                  onSelect={closeMegaMenu}
                                  alwaysNeutral
                                />
                              ) : null}
                            </div>
                          );
                        }
                        return (
                          <button
                            key={category.key}
                            type="button"
                            className={`min-h-11 w-full rounded-2xl px-3 py-2.5 text-left text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 md:min-h-11 md:py-2 ${
                              selected
                                ? 'bg-white text-purple-700 shadow-sm ring-1 ring-purple-100'
                                : 'text-slate-600 hover:bg-white hover:text-slate-900'
                            }`}
                            aria-pressed={selected}
                            aria-current={selected ? 'true' : undefined}
                            onClick={() => setActiveMegaMenuCategory(category.key)}
                          >
                            <span className="inline-flex items-center gap-2">
                              <span>{category.label}</span>
                              {category.badge === 'new' ? <NewBadge /> : null}
                            </span>
                          </button>
                        );
                      })}
                      {tgProjectMegaMenuRailItem ? (
                        <MegaMenuRailButtonLink
                          item={tgProjectMegaMenuRailItem}
                          onSelect={closeMegaMenu}
                        />
                      ) : null}
                    </div>

                    {activeMegaMenu && !megaMenuHomeRailOnly ? (
                      <section
                        aria-labelledby="mega-active-category-heading"
                        className="min-h-0 min-w-0 rounded-2xl border border-slate-100 bg-white p-3 md:h-full md:max-h-full md:overflow-y-auto md:overscroll-contain"
                      >
                        <div className="mb-3">
                          <h3 id="mega-active-category-heading" className="text-base font-bold text-slate-900">
                            {activeMegaMenu.label}
                          </h3>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{activeMegaMenu.description}</p>
                        </div>
                        <div
                          className={
                            activeMegaMenu.key === 'team'
                              ? 'grid grid-cols-1 gap-2'
                              : 'grid gap-2 sm:grid-cols-2 xl:grid-cols-3'
                          }
                        >
                          {activeMegaMenu.items.map((item) =>
                            item.subItems && item.subItems.length > 0 ? (
                              <MegaMenuExpandableItemLink key={item.key} item={item} onSelect={closeMegaMenu} />
                            ) : (
                              <MegaMenuItemLink key={item.key} item={item} onSelect={closeMegaMenu} />
                            ),
                          )}
                        </div>
                      </section>
                    ) : null}
                  </div>
                </motion.nav>
              ) : null}
            </AnimatePresence>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-6">
            <Link
              to={appHomePath}
              onClickCapture={onAppHomeSameRouteCapture}
              className="flex shrink-0 items-center gap-2 text-xl font-semibold text-slate-800"
            >
              NiData
              <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" aria-hidden />
            </Link>
            <IntraSiteSearch
              menuLinks={searchMenuLinks}
              hospitalDepartments={departments}
              scopeDepartmentIds={searchBaseDeptOptions.map((option) => option.id)}
              formVisibility={searchFormVisibility}
              departmentsLoading={departmentsLoading}
              reportComplianceSummaryCategoryId={reportComplianceSummaryCategoryId}
              setReportNurseNotesDeptNavFilterIds={setReportNurseNotesDeptNavFilterIds}
              setReportEthicDeptNavFilterIds={setReportEthicDeptNavFilterIds}
              setTeamDeptNavFilterIds={setTeamDeptNavFilterIds}
            />
            <nav className="hidden min-w-0 items-center gap-1">
              {privilegedIncidentNav ? (
                <>
                  <NavLink
                    to="/kpis"
                    onClickCapture={appHomePath === '/kpis' ? onAppHomeSameRouteCapture : undefined}
                    onClick={closeNavDropdowns}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive ? 'bg-purple-100 text-purple-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                      }`
                    }
                  >
                    KPIs ฝ่ายการพยาบาล
                  </NavLink>
                  {qualityWorkDesktopNav}
                  {incidentReportDestinations.length > 0 && (
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={incidentsDropdownOpen}
                        aria-label="รายงานอุบัติการณ์"
                        onClick={() => {
                          setIncidentsDropdownOpen((p) => !p);
                          setTeamDropdownOpen(false);
                          setKpisDropdownOpen(false);
                          setQualityWorkOpen(false);
                          setIncidentExportMenuOpen(false);
                        }}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          incidentReportDestinations.some(
                            (d) => location.pathname === d.path || location.pathname.startsWith(`${d.path}/`),
                          )
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        <span>รายงานอุบัติการณ์</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${incidentsDropdownOpen ? 'rotate-180' : ''}`}
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </button>
                      {incidentsDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-x-0 bottom-0 top-16 z-40"
                            aria-hidden
                            onClick={() => setIncidentsDropdownOpen(false)}
                          />
                          <div className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                            {incidentReportDestinations.map((dest) => (
                              <Link
                                key={dest.key}
                                to={dest.path}
                                onClick={() => setIncidentsDropdownOpen(false)}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                {dest.label}
                              </Link>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {privilegedIncidentNav &&
                    (hasMedErrorIncidentAccess || hasFallIncidentAccess) && (
                      <ShellIncidentExportNav
                        variant="desktop"
                        open={incidentExportMenuOpen}
                        onToggle={() => {
                          setIncidentExportMenuOpen((p) => !p);
                          setIncidentsDropdownOpen(false);
                          setTeamDropdownOpen(false);
                          setKpisDropdownOpen(false);
                          setQualityWorkOpen(false);
                        }}
                        hasMedError={hasMedErrorIncidentAccess}
                        hasFall={hasFallIncidentAccess}
                      />
                    )}
                  {teamDestinations.length > 0 && (
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={teamDropdownOpen}
                        aria-label="คณะทำงาน"
                        onClick={() => {
                          setTeamDropdownOpen((p) => !p);
                          setTeamSubmenuOpen(null);
                          setIncidentsDropdownOpen(false);
                          setKpisDropdownOpen(false);
                          setQualityWorkOpen(false);
                          setIncidentExportMenuOpen(false);
                        }}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          teamDestinations.some((d) =>
                            teamDestinationPathActive(d.path, location.pathname, location.search),
                          )
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        <span>คณะทำงาน</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${teamDropdownOpen ? 'rotate-180' : ''}`}
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </button>
                      {teamDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-x-0 bottom-0 top-16 z-40"
                            aria-hidden
                            onClick={() => {
                              setTeamDropdownOpen(false);
                              setTeamSubmenuOpen(null);
                            }}
                          />
                          <div className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                            {/* Flat items */}
                            {teamDestinations.some((d) => d.key === 'nn') && (
                              <Link
                                to="/report?category=nurse_notes"
                                onClick={() => {
                                  setTeamDropdownOpen(false);
                                  setTeamSubmenuOpen(null);
                                }}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                คณะทำงานบันทึกฯ
                              </Link>
                            )}
                            {teamEthicClimateDropdown}

                            {/* Submenu: Med Error */}
                            {teamDestinations.some((d) => d.key === 'med_error_incident') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() =>
                                    setTeamSubmenuOpen((cur) => (cur === 'med_error' ? null : 'med_error'))
                                  }
                                >
                                  <span className="min-w-0 flex-1 truncate">{MED_ERROR_TEAM_FULL_LABEL}</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'med_error' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/med-error-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      อุบัติการณ์ความคลาดเคลื่อนทางยา
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Submenu: Risk management (Fall) */}
                            {teamDestinations.some((d) => d.key === 'fall_incident') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() => setTeamSubmenuOpen((cur) => (cur === 'risk' ? null : 'risk'))}
                                >
                                  <span className="min-w-0 flex-1 truncate">คณะทำงานจัดการความเสี่ยง</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'risk' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[200px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/fall-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      อุบัติการณ์การพลัดตกหกล้ม
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Submenu: KPOA */}
                            {teamDestinations.some((d) => d.key === 'kpoa') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() => setTeamSubmenuOpen((cur) => (cur === 'kpoa' ? null : 'kpoa'))}
                                >
                                  <span className="min-w-0 flex-1 truncate">คณะทำงานคปอ.</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'kpoa' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/kpoa-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      บาดเจ็บ/เจ็บป่วย จากการทำงาน
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* TG stays as-is (flat) */}
                            {teamDestinations
                              .filter((d) => !['nn', 'ethic', 'med_error_incident', 'fall_incident', 'kpoa'].includes(d.key))
                              .map((dest) => (
                                <Link
                                  key={dest.key}
                                  to={dest.path}
                                  onClick={() => {
                                    setTeamDropdownOpen(false);
                                    setTeamSubmenuOpen(null);
                                  }}
                                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                >
                                  {dest.label}
                                </Link>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {isHeadWardNurse && (
                    <NavLink
                      to={appHomePath}
                      onClickCapture={onAppHomeSameRouteCapture}
                      onClick={closeNavDropdowns}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-2 text-sm font-semibold transition ${
                          isActive
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`
                      }
                    >
                      หน้าแรก
                    </NavLink>
                  )}
                  {kpisDestinations.length === 1 ? (
                    <NavLink
                      to="/kpis"
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-2 text-sm font-medium transition ${
                          isActive ? 'bg-purple-100 text-purple-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`
                      }
                    >
                      KPIs ฝ่ายการพยาบาล
                    </NavLink>
                  ) : (
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={kpisDropdownOpen}
                        aria-label="KPIs ฝ่ายการพยาบาล"
                        onClick={() => {
                          setKpisDropdownOpen((p) => !p);
                          setTeamDropdownOpen(false);
                          setIncidentsDropdownOpen(false);
                          setQualityWorkOpen(false);
                          setIncidentExportMenuOpen(false);
                        }}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          kpisDestinations.some((d) => location.pathname === d.path || location.pathname.startsWith(`${d.path}/`))
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        <span>KPIs ฝ่ายการพยาบาล</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${kpisDropdownOpen ? 'rotate-180' : ''}`}
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </button>
                      {kpisDropdownOpen && (
                        <>
                          {/* Below header (h-16) so sibling nav links (e.g. หน้าแรก) stay clickable */}
                          <div
                            className="fixed inset-x-0 bottom-0 top-16 z-40"
                            aria-hidden
                            onClick={() => setKpisDropdownOpen(false)}
                          />
                          <div className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                            {kpisDestinations.map((dest) => (
                              <Link
                                key={dest.key}
                                to={dest.path}
                                onClick={() => setKpisDropdownOpen(false)}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                {dest.label}
                              </Link>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {qualityWorkDesktopNav}
                  <NavLink
                    to={appHomePath}
                    onClickCapture={onAppHomeSameRouteCapture}
                    onClick={closeNavDropdowns}
                    style={isHeadWardNurse ? { display: 'none' } : undefined}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                      }`
                    }
                  >
                    หน้าแรก
                  </NavLink>
                  {teamDestinations.length === 1 && (
                    <NavLink
                      to={teamDestinations[0].path}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                        teamDestinationPathActive(teamDestinations[0].path, location.pathname, location.search)
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                      }`}
                    >
                      คณะทำงาน
                    </NavLink>
                  )}
                  {teamDestinations.length >= 2 && (
                    <div className="relative">
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={teamDropdownOpen}
                        aria-label="คณะทำงาน"
                        onClick={() => {
                          setTeamDropdownOpen((p) => !p);
                          setTeamSubmenuOpen(null);
                          setKpisDropdownOpen(false);
                          setIncidentsDropdownOpen(false);
                          setQualityWorkOpen(false);
                          setIncidentExportMenuOpen(false);
                        }}
                        className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                          teamDestinations.some((d) =>
                            teamDestinationPathActive(d.path, location.pathname, location.search),
                          )
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        <span>คณะทำงาน</span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${teamDropdownOpen ? 'rotate-180' : ''}`}
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </button>
                      {teamDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-x-0 bottom-0 top-16 z-40"
                            aria-hidden
                            onClick={() => {
                              setTeamDropdownOpen(false);
                              setTeamSubmenuOpen(null);
                            }}
                          />
                          <div className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                            {/* Flat items */}
                            {teamDestinations.some((d) => d.key === 'nn') && (
                              <Link
                                to="/report?category=nurse_notes"
                                onClick={() => {
                                  setTeamDropdownOpen(false);
                                  setTeamSubmenuOpen(null);
                                }}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                คณะทำงานบันทึกฯ
                              </Link>
                            )}
                            {teamEthicClimateDropdown}

                            {/* Submenu: Med Error */}
                            {teamDestinations.some((d) => d.key === 'med_error_incident') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() =>
                                    setTeamSubmenuOpen((cur) => (cur === 'med_error' ? null : 'med_error'))
                                  }
                                >
                                  <span className="min-w-0 flex-1 truncate">{MED_ERROR_TEAM_FULL_LABEL}</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'med_error' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/med-error-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      อุบัติการณ์ความคลาดเคลื่อนทางยา
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Submenu: Risk management (Fall) */}
                            {teamDestinations.some((d) => d.key === 'fall_incident') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() => setTeamSubmenuOpen((cur) => (cur === 'risk' ? null : 'risk'))}
                                >
                                  <span className="min-w-0 flex-1 truncate">คณะทำงานจัดการความเสี่ยง</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'risk' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[200px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/fall-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      อุบัติการณ์การพลัดตกหกล้ม
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Submenu: KPOA */}
                            {teamDestinations.some((d) => d.key === 'kpoa') && (
                              <div className="relative">
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() => setTeamSubmenuOpen((cur) => (cur === 'kpoa' ? null : 'kpoa'))}
                                >
                                  <span className="min-w-0 flex-1 truncate">คณะทำงานคปอ.</span>
                                  <span className="text-slate-400" aria-hidden>
                                    ›
                                  </span>
                                </button>
                                {teamSubmenuOpen === 'kpoa' && (
                                  <div className="absolute left-full top-0 z-50 ml-1 min-w-[220px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                                    <Link
                                      to="/kpoa-incidents"
                                      onClick={() => {
                                        setTeamDropdownOpen(false);
                                        setTeamSubmenuOpen(null);
                                      }}
                                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      บาดเจ็บ/เจ็บป่วย จากการทำงาน
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* TG stays as-is (flat) */}
                            {teamDestinations
                              .filter((d) => !['nn', 'ethic', 'med_error_incident', 'fall_incident', 'kpoa'].includes(d.key))
                              .map((dest) => (
                                <Link
                                  key={dest.key}
                                  to={dest.path}
                                  onClick={() => {
                                    setTeamDropdownOpen(false);
                                    setTeamSubmenuOpen(null);
                                  }}
                                  className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                >
                                  {dest.label}
                                </Link>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </nav>
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => {
                setMenuOpen((p) => !p);
                setMobileNavOpen(false);
              }}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:border-slate-300"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 text-purple-700">
                {userInitial}
              </span>
              <span className="hidden sm:inline">{displayName}</span>
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[60]"
                  aria-hidden
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full z-[70] mt-2 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  {(isAdmin || authUser?.email?.toLowerCase() === '5650414@nidata.com') && (
                    <>
                      <Link
                        to="/roles"
                        onClick={() => setMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        User Administration
                      </Link>
                      <Link
                        to="/departments"
                        onClick={() => setMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        Department Administration
                      </Link>
                      <Link
                        to="/compliance-categories"
                        onClick={() => setMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        Compliance Categories
                      </Link>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>
        {showPrivilegedShellDeptFilterRow ? (
          <div
            className="pointer-events-none sticky top-16 z-40 flex flex-wrap items-end gap-4 border-b border-slate-200/80 bg-white/95 py-2 pl-24 pr-3 shadow-sm backdrop-blur sm:gap-6 sm:pl-28 sm:pr-6"
            aria-label="ตัวกรองหน่วยงาน"
          >
            {showShellIncidentDeptFilter ? (
              <div className="pointer-events-auto min-w-0 max-w-full sm:max-w-md">
                <p className="mb-0.5 text-xs font-medium tracking-wide text-slate-500">รายงานอุบัติการณ์</p>
                <TeamDepartmentFilterBar
                  idPrefix="shell-incident-dept"
                  options={incidentNavDeptOptions}
                  selectedIds={incidentDeptNavFilterIds}
                  onChange={setIncidentDeptNavFilterIds}
                />
              </div>
            ) : null}
            {showShellTeamDeptFilter ? (
              <div className="pointer-events-auto min-w-0 max-w-full sm:max-w-md">
                <TeamDepartmentFilterBar
                  idPrefix="shell-team-dept"
                  options={teamNavDeptOptions}
                  selectedIds={teamDeptNavFilterIds}
                  onChange={setTeamDeptNavFilterIds}
                />
              </div>
            ) : null}
            {showShellReportNurseNotesDeptFilter ? (
              <div className="pointer-events-auto min-w-0 max-w-full sm:max-w-md">
                <TeamDepartmentFilterBar
                  idPrefix="shell-report-nn-dept"
                  options={teamNavDeptOptions}
                  selectedIds={reportNurseNotesDeptNavFilterIds}
                  onChange={setReportNurseNotesDeptNavFilterIds}
                />
              </div>
            ) : null}
            {showShellNurseNotesPeriodControls ? <NurseNotesShellPeriodControls /> : null}
            {showShellReportEthicDeptFilter ? (
              <div className="pointer-events-auto min-w-0 max-w-full sm:max-w-md">
                <TeamDepartmentFilterBar
                  idPrefix="shell-report-ethic-dept"
                  options={teamNavDeptOptions}
                  selectedIds={reportEthicDeptNavFilterIds}
                  onChange={setReportEthicDeptNavFilterIds}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        </>
      )}
      <main className="flex-1">
        {/* ComplianceHub reads this to avoid duplicating NiData / user / quality chrome when the shell header is visible */}
        <Outlet
          context={{
            appShellShowsMainNav: true,
            shellTeamDeptFilterActive: showShellTeamDeptFilter,
            shellReportNurseNotesDeptFilterActive: showShellReportNurseNotesDeptFilter,
            shellReportEthicDeptFilterActive: showShellReportEthicDeptFilter,
          }}
        />
      </main>
    </div>
  );
};
